import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Select from "../../../components/Select";
import {
  createEdgeGateway,
  deleteEdgeGateway,
  getEdgeRuntimePackageReadiness,
  getPLCDeploymentSigningPolicy,
  listEdgeControlPlaneCandidates,
  listEdgeGateways,
  listEdgeHardwareProfiles,
  rollbackEdgeGatewayRuntime,
  rotateEdgeGatewayToken,
  updateEdgeGatewayEnrollmentSettings,
  updateEdgeGatewayRuntime,
  updateEdgeGatewayRuntimeTrust,
} from "../../../api";
import { useNotify } from "../../../ToastContext";
import { usePermission } from "../../../UserContext";
import chevronIcon from "../../../assets/chevron-right.svg";
import { EdgeConnectionsPage } from "./EdgeConnectorsModal";
import {
  CUSTOM_URL,
  DEFAULT_EDGE_CLOUD_TRANSPORT,
  DEFAULT_EDGE_HARDWARE_PROFILE,
  DEFAULT_EDGE_SYNC_POLICY,
  EDGE_CLOUD_TRANSPORT_OPTIONS,
  browserFallbackCloudURL,
  edgeGatewayEnrollmentPayload,
  edgeGatewayEnrollmentSettingsPayload,
  edgeHardwareProfileFromCatalog,
  enrollmentHardwareProfile,
  hardwareProfileOptions,
  isLocalOnlyURL,
  normalizeHardwareProfileCatalog,
  randomInstallID,
  runtimeReenrollmentSettings,
  runtimeConnectionRequestFromSearch,
  runtimeEnrollmentReadiness,
  runtimeSyncClassOptions,
} from "./platformEdgeRuntimes.model";
import {
  ConfirmModal,
  EnrollmentSettingsModal,
  HardwareIOPage,
  PLCProgramsPage,
  RuntimeDetailView,
  RuntimeRollbackModal,
  RuntimeTrustUpdateModal,
  RuntimeRow,
  SetupModal,
} from "./platformEdgeRuntimes.components";

const runtimeSettingsSaveErrorMessage = (error) => {
  const message = String(error?.message || "");
  if (/stop every PLC application/i.test(message)) {
    return "Stop every PLC application on this device, wait for it to confirm they are stopped, then try again.";
  }
  const retryMinutes = message.match(/try again in about\s+(\d+)\s+minutes?/i)?.[1];
  if (retryMinutes) {
    return `This device disconnected recently. Try again in about ${retryMinutes} minutes, or reconnect it and stop its PLC applications first.`;
  }
  return "Could not save these runtime settings. Check the device status and try again.";
};

// Pure model helpers and the modal / detail-view components moved into sibling
// modules (platformEdgeRuntimes.model / platformEdgeRuntimes.components) so this
// file stays focused on the page. The module's long-standing public API
// (helpers used by tests, RuntimeRow) is re-exported unchanged
// at the bottom, so importers keep importing from "./platform-edge-runtimes".

function PlcEdgeRuntimesPage({ embedded = false }) {
  const canConfigure = usePermission("configure");
  const canWrite = usePermission("write");
  const notify = useNotify();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyID, setBusyID] = useState("");
  const [connectorsInitialKind, setConnectorsInitialKind] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [hardwareProfile, setHardwareProfile] = useState(DEFAULT_EDGE_HARDWARE_PROFILE);
  const [hardwareProfiles, setHardwareProfiles] = useState(() => normalizeHardwareProfileCatalog(null));
  const [cloudTransport, setCloudTransport] = useState(DEFAULT_EDGE_CLOUD_TRANSPORT);
  const [syncPolicy, setSyncPolicy] = useState({ ...DEFAULT_EDGE_SYNC_POLICY });
  const [urlChoice, setURLChoice] = useState("");
  const [customURL, setCustomURL] = useState(browserFallbackCloudURL());
  const [urlCandidates, setURLCandidates] = useState([]);
  const [availableCloudTransports, setAvailableCloudTransports] = useState([DEFAULT_EDGE_CLOUD_TRANSPORT]);
  const [urlLoading, setURLLoading] = useState(true);
  const [signingPolicy, setSigningPolicy] = useState(null);
  const [signingLoading, setSigningLoading] = useState(true);
  const [signingError, setSigningError] = useState("");
  const [packageReadiness, setPackageReadiness] = useState(null);
  const [packageReadinessLoading, setPackageReadinessLoading] = useState(true);
  const [enrollment, setEnrollment] = useState(null);
  const [enrollmentSettingsGateway, setEnrollmentSettingsGateway] = useState(null);
  const [pendingConnection, setPendingConnection] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {kind: "update"|"rollback"|"trust"|"rotate"|"remove", gateway}
  const mountedRef = useRef(true);
  const loadGenerationRef = useRef(0);
  const hardwareIODeepLinkHandledRef = useRef(false);

  const cloudURL = (urlChoice && urlChoice !== CUSTOM_URL ? urlChoice : customURL).trim();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPackageReadinessLoading(true);
    getEdgeRuntimePackageReadiness(enrollmentHardwareProfile(hardwareProfile))
      .then((readiness) => {
        if (!cancelled) setPackageReadiness(readiness);
      })
      .catch(() => {
        if (!cancelled) setPackageReadiness({ ready: false });
      })
      .finally(() => {
        if (!cancelled) setPackageReadinessLoading(false);
      });
    return () => { cancelled = true; };
  }, [hardwareProfile]);

  useEffect(() => {
    let cancelled = false;
    listEdgeHardwareProfiles()
      .then((catalog) => { if (!cancelled) setHardwareProfiles(normalizeHardwareProfileCatalog(catalog)); })
      .catch(() => {
        // Older control planes do not expose the catalog. The checked-in
        // profiles remain a complete and safe enrollment fallback.
      });
    return () => { cancelled = true; };
  }, []);

  const loadGateways = useCallback(async () => {
    const generation = ++loadGenerationRef.current;
    try {
      const list = await listEdgeGateways();
      if (!mountedRef.current || generation !== loadGenerationRef.current) return;
      const next = Array.isArray(list) ? list : [];
      setGateways(next);
    } catch {
      if (!mountedRef.current || generation !== loadGenerationRef.current) return;
      notify({ type: "error", title: "Runtimes unavailable", message: "Could not load runtimes. Try again." });
    } finally {
      if (mountedRef.current && generation === loadGenerationRef.current) setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      if (cancelled) return;
      await loadGateways();
    };
    refresh();
    const timer = setInterval(refresh, 10000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [loadGateways]);

  // Runtime-scoped deep links: a bare ?runtime=<id> opens that runtime's
  // detail view; adding &connection= (or the legacy hardware_io alias) also
  // opens the unified connection flow on top of it.
  useEffect(() => {
    if (loading || hardwareIODeepLinkHandledRef.current || typeof window === "undefined") return;
    const request = runtimeConnectionRequestFromSearch(window.location.search);
    if (!request) return;
    hardwareIODeepLinkHandledRef.current = true;
    const gateway = gateways.find((item) => item.id === request.runtimeID);
    if (gateway) {
      if (request.connection || request.legacy) {
        setConnectorsInitialKind(request.connection || "gpio");
      }
    } else {
      notify({
        type: "warning",
        title: "Edge runtime not found",
        message: "The selected runtime is no longer enrolled. Choose another runtime from this page.",
      });
    }
    // Normalize the URL: the runtime selection stays (it is the detail-view
    // state); one-shot connection params are consumed.
    const next = new URLSearchParams(window.location.search);
    next.delete("hardware_io");
    next.delete("connection");
    next.delete("tab");
    if (gateway) {
      next.set("runtime", gateway.id);
      if (request.connection || request.legacy) next.set("view", "connections");
    }
    else next.delete("runtime");
    setSearchParams(next, { replace: true });
  }, [gateways, loading, notify, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    getPLCDeploymentSigningPolicy()
      .then((policy) => {
        if (cancelled) return;
        setSigningPolicy(policy);
        setSigningError("");
      })
      .catch(() => {
        if (cancelled) return;
        setSigningPolicy(null);
        setSigningError("Deployment security status is temporarily unavailable.");
      })
      .finally(() => { if (!cancelled) setSigningLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    listEdgeControlPlaneCandidates()
      .then((data) => {
        if (cancelled) return;
        const candidates = Array.isArray(data?.candidates) ? data.candidates : [];
        const transports = Array.isArray(data?.cloud_transports)
          ? data.cloud_transports.filter((value) => ["websocket", "mqtt"].includes(value))
          : [DEFAULT_EDGE_CLOUD_TRANSPORT];
        setURLCandidates(candidates);
        setAvailableCloudTransports(transports.includes(DEFAULT_EDGE_CLOUD_TRANSPORT)
          ? transports
          : [DEFAULT_EDGE_CLOUD_TRANSPORT, ...transports]);
        setCloudTransport((current) => transports.includes(current) ? current : DEFAULT_EDGE_CLOUD_TRANSPORT);
        const preferred = candidates.find((c) => !isLocalOnlyURL(c.url)) || candidates[0];
        if (preferred?.url) setURLChoice(preferred.url);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setURLLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const gpioHandler = (event) => setPendingConnection({ ...(event.detail?.payload || {}), kind: "gpio", _tick: event.detail?.tick || Date.now() });
    const modbusHandler = (event) => setPendingConnection({ ...(event.detail?.payload || {}), kind: "modbus-rtu", _tick: event.detail?.tick || Date.now() });
    window.addEventListener("gw-ai-propose-edge-gpio-page", gpioHandler);
    window.addEventListener("gw-ai-propose-edge-modbus-rtu-page", modbusHandler);
    return () => {
      window.removeEventListener("gw-ai-propose-edge-gpio-page", gpioHandler);
      window.removeEventListener("gw-ai-propose-edge-modbus-rtu-page", modbusHandler);
    };
  }, []);

  useEffect(() => {
    if (!pendingConnection?.gateway_id || pendingConnection._opened) return;
    const match = gateways.find((gateway) => gateway.id === pendingConnection.gateway_id);
    if (!match) return;
    setConnectorsInitialKind(pendingConnection.kind || "");
    const next = new URLSearchParams(searchParams);
    next.set("runtime", match.id);
    next.set("view", "connections");
    setSearchParams(next);
    setPendingConnection((current) => current ? { ...current, _opened: true } : current);
  }, [gateways, pendingConnection, searchParams, setSearchParams]);

  const enroll = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      notify({ type: "error", title: "Name required", message: "Give the runtime a name like \"Packaging Pi\"." });
      return;
    }
    if (!cloudURL) {
      notify({ type: "error", title: "Control Seat URL needed", message: "Enter a URL the device can reach before enrolling." });
      return;
    }
    const readiness = runtimeEnrollmentReadiness({
      cloudURL, signingLoading, signingPolicy, packageReadinessLoading, packageReadiness,
    });
    if (!readiness.ready) {
      notify({ type: "error", title: readiness.title, message: readiness.message });
      return;
    }
    if (!syncPolicy.includeGPIO && !syncPolicy.includePLC && !syncPolicy.includeConnectors && !syncPolicy.includeOutputEvidence) {
      notify({ type: "error", title: "Choose synchronization data", message: "Select at least one data class for this runtime to synchronize." });
      return;
    }
    try {
      new URL(cloudURL);
    } catch {
      notify({ type: "error", title: "Invalid URL", message: "Use a full URL like https://controlseat.example.com." });
      return;
    }
    setBusyID("create");
    try {
      // The install identity is an internal pairing handle — generated
      // here, delivered inside the setup script, shown later on the row.
      const installID = randomInstallID();
      const result = await createEdgeGateway(edgeGatewayEnrollmentPayload({
        name: trimmedName,
        installID,
        hardwareProfile,
        syncPolicy,
        cloudTransport,
      }));
      if (!result?.gateway || !result?.bootstrap_secret) {
        throw new Error("The runtime was added without a one-time setup secret. Reload before retrying.");
      }
      setEnrollment({ gateway: result.gateway, bootstrapSecret: result.bootstrap_secret, cloudURL, installID, syncPolicy: { ...syncPolicy }, hardwareProfile, cloudTransport });
      setName("");
      await loadGateways();
      notify({ type: "success", title: "Edge runtime enrolled", message: "Run the setup command on the device to connect it." });
    } catch {
      notify({ type: "error", title: "Enrollment failed", message: "Could not enroll this runtime. Check the settings and try again." });
    } finally {
      setBusyID("");
    }
  };

  const rotateToken = async (gateway) => {
    setBusyID(`rotate:${gateway.id}`);
    try {
      const result = await rotateEdgeGatewayToken(gateway.id);
      if (!result?.bootstrap_secret) {
        throw new Error("The runtime credential was revoked without a replacement setup secret. Reload before retrying.");
      }
      setConfirmAction(null);
      const reenrollment = runtimeReenrollmentSettings(gateway);
      setEnrollment({
        gateway,
        bootstrapSecret: result.bootstrap_secret,
        cloudURL,
        installID: gateway.install_id,
        syncPolicy: reenrollment.syncPolicy,
        hardwareProfile: reenrollment.hardwareProfile,
        cloudTransport: reenrollment.cloudTransport,
      });
      await loadGateways();
      notify({ type: "success", title: "Setup secret created", message: `${gateway.name} disconnects until the new setup command runs on it.` });
    } catch {
      notify({ type: "error", title: "Reconnect failed", message: "Could not create a fresh setup command. Try again." });
    } finally {
      setBusyID("");
    }
  };

  const saveEnrollmentSettings = async ({ hardwareProfile: reviewedProfile, syncPolicy: reviewedPolicy, cloudTransport: reviewedTransport }) => {
    const gateway = enrollmentSettingsGateway;
    if (!gateway) return;
    if (!edgeHardwareProfileFromCatalog(reviewedProfile, hardwareProfiles)) {
      notify({ type: "error", title: "Hardware profile required", message: "Choose the hardware attached to this runtime." });
      return;
    }
    if (!reviewedPolicy.includeGPIO && !reviewedPolicy.includePLC && !reviewedPolicy.includeConnectors && !reviewedPolicy.includeOutputEvidence) {
      notify({ type: "error", title: "Choose synchronization data", message: "Select at least one data class for this runtime to synchronize." });
      return;
    }
    if (!Number.isSafeInteger(gateway.enrollment_settings_revision) || gateway.enrollment_settings_revision < 0) {
      notify({ type: "error", title: "Reload required", message: "Reload the runtime before reviewing its setup settings." });
      return;
    }
    setBusyID(`enrollment-settings:${gateway.id}`);
    try {
      const result = await updateEdgeGatewayEnrollmentSettings(
        gateway.id,
        edgeGatewayEnrollmentSettingsPayload({
          gateway,
          hardwareProfile: reviewedProfile,
          syncPolicy: reviewedPolicy,
          cloudTransport: reviewedTransport,
        }),
      );
      const updatedGateway = result?.gateway;
      if (!updatedGateway || !result?.bootstrap_secret) {
        throw new Error("The runtime settings were saved without a replacement setup secret. Reload before retrying.");
      }
      setEnrollmentSettingsGateway(null);
      setEnrollment({
        gateway: updatedGateway,
        bootstrapSecret: result.bootstrap_secret,
        cloudURL,
        installID: updatedGateway.install_id || gateway.install_id,
        syncPolicy: { ...reviewedPolicy },
        hardwareProfile: reviewedProfile,
        cloudTransport: reviewedTransport,
      });
      await loadGateways();
      notify({
        type: "success",
        title: "Setup settings saved",
        message: `Run the new installer on ${updatedGateway.name || gateway.name} to reconnect it.`,
      });
    } catch (err) {
      // Two different conditions share the 409 status here, and they need
      // opposite handling:
      //
      //   stale review   the saved settings moved under the operator, so the
      //                  reviewed values are genuinely out of date. Close the
      //                  panel and ask them to review again.
      //   maintenance    the runtime is scanning, or went offline too recently
      //                  to recover. Nothing about the review is wrong — it is
      //                  simply not the moment. Keep the panel open so the
      //                  operator can save again once the condition clears
      //                  instead of redoing the whole review.
      //
      // Treating every 409 as staleness discarded the operator's work for a
      // purely retryable condition, and buried the server's explanation behind
      // a fixed "the runtime changed" line — which is why a maintenance refusal
      // read as a phantom concurrent edit.
      const staleReview = err?.status === 409
        && /enrollment settings (changed|require operator review)/i.test(err?.message || "");
      if (staleReview) setEnrollmentSettingsGateway(null);
      notify({
        type: "error",
        title: "Setup settings not saved",
        message: staleReview
          ? "The runtime changed while you were reviewing it. Reopen setup review and confirm the latest settings."
          : runtimeSettingsSaveErrorMessage(err),
      });
      await loadGateways();
    } finally {
      setBusyID("");
    }
  };

  const updateRuntime = async (gateway) => {
    setBusyID(`update:${gateway.id}`);
    try {
      await updateEdgeGatewayRuntime(gateway.id);
      setConfirmAction(null);
      await loadGateways();
      notify({
        type: "success",
        title: "Runtime update started",
        message: `${gateway.name} may briefly appear offline while it installs and restarts. This page will refresh automatically.`,
      });
    } catch {
      notify({ type: "error", title: "Runtime update failed", message: "Could not start the runtime update. Check that the device is online and try again." });
    } finally {
      setBusyID("");
    }
  };

  const rollbackRuntime = async (gateway, confirmation) => {
    setBusyID(`rollback:${gateway.id}`);
    try {
      await rollbackEdgeGatewayRuntime(gateway.id, confirmation);
      setConfirmAction(null);
      await loadGateways();
      notify({
        type: "success",
        title: "Runtime rollback queued",
        message: `${gateway.name} will verify and restore the selected version.`,
      });
    } catch {
      notify({ type: "error", title: "Runtime rollback failed", message: "Could not start the rollback. Check that the device is online and try again." });
    } finally {
      setBusyID("");
    }
  };

  const updateRuntimeTrust = async (gateway, confirmation) => {
    setBusyID(`trust:${gateway.id}`);
    try {
      await updateEdgeGatewayRuntimeTrust(gateway.id, confirmation);
      setConfirmAction(null);
      await loadGateways();
      notify({
        type: "success",
        title: "Device security update queued",
        message: `${gateway.name} will verify and apply the security update.`,
      });
    } catch {
      notify({ type: "error", title: "Device security update failed", message: "Could not start the device security update." });
    } finally {
      setBusyID("");
    }
  };

  const removeGateway = async (gateway) => {
    setBusyID(`delete:${gateway.id}`);
    try {
      await deleteEdgeGateway(gateway.id);
      setConfirmAction(null);
      if (enrollment?.gateway?.id === gateway.id) setEnrollment(null);
      if (searchParams.get("runtime") === gateway.id) {
        const next = new URLSearchParams(searchParams);
        next.delete("runtime");
        setSearchParams(next, { replace: true });
      }
      await loadGateways();
      notify({ type: "success", title: "Edge runtime removed", message: `${gateway.name} was removed.` });
    } catch {
      notify({ type: "error", title: "Remove failed", message: "Could not remove this runtime. Try again." });
    } finally {
      setBusyID("");
    }
  };

  const urlOptions = [
    ...urlCandidates.map((candidate) => ({
      value: candidate.url,
      label: `${candidate.url} — ${candidate.label}`,
    })),
    { value: CUSTOM_URL, label: "Custom URL…" },
  ];
  const showCustomInput = urlCandidates.length === 0 || urlChoice === CUSTOM_URL;
  const onlineCount = gateways.filter((g) => g.status === "online").length;
  const enrollmentReadiness = runtimeEnrollmentReadiness({
    cloudURL, signingLoading, signingPolicy, packageReadinessLoading, packageReadiness,
  });
  const selectedHardwareProfile = edgeHardwareProfileFromCatalog(hardwareProfile, hardwareProfiles);
  const enrollmentHardwareOptions = hardwareProfileOptions(hardwareProfiles);
  const selectedRuntimeID = (searchParams.get("runtime") || "").trim();
  const selectedGateway = selectedRuntimeID ? gateways.find((gateway) => gateway.id === selectedRuntimeID) || null : null;
  const selectedRuntimeView = (searchParams.get("view") || "").trim();
  const openRuntime = (gateway) => {
    const next = new URLSearchParams(searchParams);
    next.set("runtime", gateway.id);
    setSearchParams(next);
  };
  const closeRuntime = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("runtime");
    next.delete("view");
    setSearchParams(next);
  };
  const openConnections = (gateway, kind = "") => {
    setConnectorsInitialKind(kind);
    const next = new URLSearchParams(searchParams);
    next.set("runtime", gateway.id);
    next.set("view", "connections");
    setSearchParams(next);
  };
  const closeConnections = () => {
    setConnectorsInitialKind("");
    setPendingConnection(null);
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next);
  };
  // Generic sub-view routing for views that carry no extra state of their own.
  // openConnections stays separate because it also seeds the connector kind.
  const openRuntimeView = (gateway, view) => {
    const next = new URLSearchParams(searchParams);
    next.set("runtime", gateway.id);
    next.set("view", view);
    setSearchParams(next);
  };
  const closeRuntimeView = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    setSearchParams(next);
  };

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        {selectedRuntimeID ? (
          selectedGateway ? (selectedRuntimeView === "applications" ? (
            <PLCProgramsPage
              gateway={selectedGateway}
              canWrite={canWrite}
              onClose={closeRuntimeView}
              onChanged={loadGateways}
            />
          ) : ["hardware", "fr202"].includes(selectedRuntimeView) ? (
            <HardwareIOPage
              gateway={selectedGateway}
              canConfigure={canConfigure}
              onClose={closeRuntimeView}
              onConnections={() => openConnections(selectedGateway, "")}
              onChanged={loadGateways}
            />
          ) : selectedRuntimeView === "connections" ? (
            <EdgeConnectionsPage
              gateway={selectedGateway}
              canConfigure={canConfigure}
              initialKind={connectorsInitialKind}
              initialDraft={pendingConnection?.gateway_id === selectedGateway.id ? pendingConnection : null}
              onClose={closeConnections}
              onChanged={loadGateways}
            />
          ) : (
            <RuntimeDetailView
              gateway={selectedGateway}
              hardwareProfiles={hardwareProfiles}
              canConfigure={canConfigure}
              canWrite={canWrite}
              busyID={busyID}
              onBack={closeRuntime}
              onApplications={() => openRuntimeView(selectedGateway, "applications")}
              onConnections={(kind) => openConnections(selectedGateway, kind)}
              onHardwareSetup={() => openRuntimeView(selectedGateway, "hardware")}
              onChanged={loadGateways}
              onUpdateRuntime={() => setConfirmAction({ kind: "update", gateway: selectedGateway })}
              onRollbackRuntime={() => setConfirmAction({ kind: "rollback", gateway: selectedGateway })}
              onUpdateTrust={() => setConfirmAction({ kind: "trust", gateway: selectedGateway })}
              onManualUpdate={() => setConfirmAction({ kind: "manual-update", gateway: selectedGateway })}
              onReviewSettings={() => setEnrollmentSettingsGateway(selectedGateway)}
              onRotate={() => setConfirmAction({ kind: "rotate", gateway: selectedGateway })}
              onRemove={() => setConfirmAction({ kind: "remove", gateway: selectedGateway })}
            />
          )) : (
            loading ? <div className="gwHint">Loading edge runtimes…</div> : (
              <div className="gwEmptyState gwEmptyCompact">
                <div className="gwEmptyTitle">Runtime not found</div>
                <div className="gwEmptyBody">This runtime is no longer enrolled.</div>
                <div className="gwEmptyActions">
                  <button type="button" className="gwGhostBtn" onClick={closeRuntime}>Back to runtimes</button>
                </div>
              </div>
            )
          )
        ) : (
        <>
        {!embedded ? <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">PLC Edge Runtimes</div>
            <div className="gwPageSubtitle">
              Add and manage the Pis and industrial PCs that run PLC applications and I/O connections.
            </div>
          </div>
          <button type="button" className="gwGhostBtn" onClick={loadGateways}>
            Refresh
          </button>
        </div> : null}

        <div className="gwCardSection" data-gw-card="plc_edge_enroll">
          <div className="gwSectionTitle">Enroll a runtime</div>
          <div className="gwCardSectionBody">
            {signingLoading ? <div className="gwHint">Checking deployment security…</div> : signingPolicy?.active !== true ? (
              <div className="gwNotice isWarning plcRuntimeSigningGate">
                <div><strong>Finish deployment security setup</strong><span>{signingError || "Deployment security must be ready before the first runtime is enrolled."}</span></div>
                <Link className="gwGhostBtn" to="/plc-deployments?setup=signing">Open deployment security</Link>
              </div>
            ) : null}
            {packageReadinessLoading ? <div className="gwHint">Checking runtime installer availability…</div> : packageReadiness?.ready !== true ? (
              <div className="gwNotice isWarning">
                <div><strong>Runtime installer unavailable</strong></div>
                <div>The runtime installer is not ready yet. Try again after the next software update.</div>
              </div>
            ) : null}
            <div className="gwField">
              <label className="gwLabel">Hardware profile</label>
              <Select
                value={hardwareProfile}
                onChange={setHardwareProfile}
                options={enrollmentHardwareOptions}
                disabled={!canConfigure}
              />
              {selectedHardwareProfile ? (
                <div className="gwHint plcRuntimeHardwareSummary">
                  {hardwareProfile === DEFAULT_EDGE_HARDWARE_PROFILE
                    ? "Control Seat will inventory this Linux host. Choose a specific model when its built-in I/O needs device access."
                    : [selectedHardwareProfile.product, selectedHardwareProfile.operatingSystem, selectedHardwareProfile.ioSummary].filter(Boolean).join(" · ")}
                </div>
              ) : null}
            </div>
            <div className="gwFieldRow">
              <div className="gwField" style={{ flex: 1 }}>
                <label className="gwLabel">Runtime name</label>
                <input
                  className="gwInput"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") enroll(); }}
                  placeholder="Packaging Pi"
                  disabled={!canConfigure}
                />
              </div>
            </div>
            <div className="gwField">
              <label className="gwLabel">Connects to</label>
              <input className="gwInput" value={urlLoading ? "Finding a reachable URL…" : cloudURL} readOnly aria-readonly="true" />
              {/* This field is a read-only view of the resolved address; the
                  control that changes it is "Control Seat URL override" inside
                  Advanced enrollment settings below. Say so — an instruction to
                  override, next to a field that cannot be typed in and with no
                  pointer to the real control, reads as a broken input. */}
              <div className="gwHint">Control Seat selects a device-reachable address automatically. To use a different one, open Advanced enrollment settings below and set the Control Seat URL override.</div>
            </div>
            <div className="gwField">
              <label className="gwLabel">Cloud transport</label>
              <Select
                value={cloudTransport}
                onChange={setCloudTransport}
                options={EDGE_CLOUD_TRANSPORT_OPTIONS.filter((option) => availableCloudTransports.includes(option.value))}
                disabled={!canConfigure}
              />
              <div className="gwHint">
                {cloudTransport === "mqtt"
                  ? "Uses the tenant MQTT broker with per-runtime encrypted commands and telemetry. Plant MQTT connections remain separate."
                  : "Uses the standard outbound WebSocket control connection."}
              </div>
            </div>
            {isLocalOnlyURL(cloudURL) ? (
              <div className="gwNotice isWarning">
                <div><strong>Localhost cannot enroll a remote runtime</strong></div>
                <div>Open Advanced enrollment settings and choose this computer's LAN address, a VPN hostname, or a production HTTPS URL.</div>
              </div>
            ) : null}
            <details className="plcRuntimeEnrollAdvanced">
              <summary className="gwLabel">
                <img src={chevronIcon} alt="" className="gwIcon gwDisclosureChevron" />
                Advanced enrollment settings
              </summary>
              <div className="gwFormStack plcRuntimeEnrollAdvancedBody">
            <div className="gwField">
              <label className="gwLabel">Control Seat URL override</label>
              {urlCandidates.length > 0 && (
                <Select
                  value={urlChoice}
                  onChange={setURLChoice}
                  options={urlOptions}
                  disabled={!canConfigure}
                />
              )}
              {showCustomInput && (
                <input
                  className="gwInput"
                  value={customURL}
                  onChange={(e) => setCustomURL(e.target.value)}
                  placeholder="https://controlseat.example.com"
                  disabled={!canConfigure}
                />
              )}
              <div className="gwHint">
                {urlLoading
                  ? "Finding a device-reachable URL…"
                  : "The device must be able to reach this address over the network."}
              </div>
            </div>
            <div className="gwField">
              <label className="gwLabel">Cloud synchronization</label>
              <div className="gwHint">Choose which ordinary runtime data is durably relayed. PLC execution remains local and does not depend on these selections.</div>
              <div className="gwCheckboxGrid">
                {runtimeSyncClassOptions(hardwareProfile).map(([field, label]) => (
                  <label className="gwCheckboxRow" key={field}>
                    <input
                      type="checkbox"
                      checked={Boolean(syncPolicy[field])}
                      disabled={!canConfigure}
                      onChange={(event) => setSyncPolicy((current) => ({ ...current, [field]: event.target.checked }))}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="gwFieldRow">
                <div className="gwField" style={{ flex: 1 }}>
                  <label className="gwLabel">Offline retention</label>
                  <Select
                    value={String(syncPolicy.retentionSeconds)}
                    onChange={(value) => setSyncPolicy((current) => ({ ...current, retentionSeconds: Number(value) }))}
                    options={[
                      { value: "21600", label: "6 hours" },
                      { value: "86400", label: "24 hours" },
                      { value: "259200", label: "3 days" },
                      { value: "604800", label: "7 days" },
                    ]}
                    disabled={!canConfigure}
                  />
                </div>
                <div className="gwField" style={{ flex: 1 }}>
                  <label className="gwLabel">Snapshot interval</label>
                  <Select
                    value={String(syncPolicy.snapshotSeconds)}
                    onChange={(value) => setSyncPolicy((current) => ({ ...current, snapshotSeconds: Number(value) }))}
                    options={[
                      { value: "5", label: "5 seconds" },
                      { value: "10", label: "10 seconds" },
                      { value: "30", label: "30 seconds" },
                      { value: "60", label: "1 minute" },
                    ]}
                    disabled={!canConfigure}
                  />
                </div>
              </div>
              <div className="gwFieldRow">
                <div className="gwField" style={{ flex: 1 }}>
                  <label className="gwLabel">Event capacity</label>
                  <Select
                    value={String(syncPolicy.eventCapacity)}
                    onChange={(value) => setSyncPolicy((current) => ({ ...current, eventCapacity: Number(value) }))}
                    options={[
                      { value: "1000", label: "1,000 events" },
                      { value: "10000", label: "10,000 events" },
                      { value: "50000", label: "50,000 events" },
                    ]}
                    disabled={!canConfigure}
                  />
                </div>
                <div className="gwField" style={{ flex: 1 }}>
                  <label className="gwLabel">Disk capacity</label>
                  <Select
                    value={String(syncPolicy.byteCapacity)}
                    onChange={(value) => setSyncPolicy((current) => ({ ...current, byteCapacity: Number(value) }))}
                    options={[
                      { value: "67108864", label: "64 MiB" },
                      { value: "268435456", label: "256 MiB" },
                      { value: "536870912", label: "512 MiB" },
                    ]}
                    disabled={!canConfigure}
                  />
                </div>
              </div>
            </div>
              </div>
            </details>
            <div className="gwHeaderActions">
              <button
                type="button"
                className="gwPrimaryBtn"
                disabled={!canConfigure || busyID === "create" || !enrollmentReadiness.ready}
                onClick={enroll}
              >
                {busyID === "create" ? "Enrolling…" : "Enroll runtime"}
              </button>
            </div>
          </div>
        </div>

        <div className="gwCardSection" data-gw-card="plc_edge_runtimes">
          <div className="gwSectionTitleRow gwSectionTitle">
            <span>Enrolled runtimes</span>
            <span className="gwSectionBadge">
              {gateways.length === 0
                ? "None yet"
                : `${onlineCount} of ${gateways.length} online`}
            </span>
          </div>
          <div className="gwCardSectionBody">
            {loading && <div className="gwHint">Loading edge runtimes…</div>}
            {!loading && gateways.length === 0 && (
              <div className="gwEmptyState gwEmptyCompact">
                <div className="gwEmptyTitle">No edge runtimes yet</div>
                <div className="gwEmptyBody">
                  Add an industrial PC or Raspberry Pi above, run its one-line installer, then open the runtime to connect I/O and applications.
                </div>
              </div>
            )}
            {!loading && gateways.length > 0 && (
              <div className="gwEdgeRuntimeList">
                {gateways.map((gateway) => (
                  <RuntimeRow
                    key={gateway.id}
                    gateway={gateway}
                    hardwareProfiles={hardwareProfiles}
                    onOpen={() => openRuntime(gateway)}
                    onReview={canConfigure && canWrite ? () => setEnrollmentSettingsGateway(gateway) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        </>
        )}
      </section>

      {enrollment && <SetupModal enrollment={enrollment} hardwareProfiles={hardwareProfiles} online={gateways.find((gateway) => gateway.id === enrollment.gateway?.id)?.status === "online"} onClose={() => setEnrollment(null)} />}

      {enrollmentSettingsGateway && (
        <EnrollmentSettingsModal
          gateway={enrollmentSettingsGateway}
          hardwareProfiles={hardwareProfiles}
          availableCloudTransports={availableCloudTransports}
          busy={busyID === `enrollment-settings:${enrollmentSettingsGateway.id}`}
          onClose={() => setEnrollmentSettingsGateway(null)}
          onConfirm={saveEnrollmentSettings}
        />
      )}

      {confirmAction?.kind === "update" && (
        <ConfirmModal
          title="Update runtime"
          body={`Update the runtime on ${confirmAction.gateway.name}? Stop all PLC applications first. The device verifies the update, installs it, and restarts. Its version refreshes after it reconnects.`}
          confirmLabel={busyID === `update:${confirmAction.gateway.id}` ? "Starting update…" : "Update runtime"}
          busy={busyID === `update:${confirmAction.gateway.id}`}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => updateRuntime(confirmAction.gateway)}
        />
      )}
      {confirmAction?.kind === "rollback" && (
        <RuntimeRollbackModal
          gateway={confirmAction.gateway}
          busy={busyID === `rollback:${confirmAction.gateway.id}`}
          onClose={() => setConfirmAction(null)}
          onConfirm={(confirmation) => rollbackRuntime(confirmAction.gateway, confirmation)}
        />
      )}
      {confirmAction?.kind === "trust" && (
        <RuntimeTrustUpdateModal
          gateway={confirmAction.gateway}
          busy={busyID === `trust:${confirmAction.gateway.id}`}
          onClose={() => setConfirmAction(null)}
          onConfirm={(confirmation) => updateRuntimeTrust(confirmAction.gateway, confirmation)}
        />
      )}
      {confirmAction?.kind === "manual-update" && (
        <ConfirmModal
          title="Prepare manual upgrade"
          body={`${confirmAction.gateway.name} needs setup to be run again for this update. Stop its PLC applications, create a fresh setup command, and run it on the device. The device stays disconnected until setup finishes.`}
          confirmLabel={busyID === `rotate:${confirmAction.gateway.id}` ? "Minting…" : "Get setup command"}
          busy={busyID === `rotate:${confirmAction.gateway.id}`}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => rotateToken(confirmAction.gateway)}
        />
      )}
      {confirmAction?.kind === "rotate" && (
        <ConfirmModal
          title="Reconnect runtime"
          body={`Reconnect ${confirmAction.gateway.name}? Every PLC application must be stopped and the runtime must report a fresh maintenance state. This revokes its current credential; the device stays disconnected until its local services are stopped and setup runs again.`}
          confirmLabel={busyID === `rotate:${confirmAction.gateway.id}` ? "Creating…" : "Create setup secret"}
          busy={busyID === `rotate:${confirmAction.gateway.id}`}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => rotateToken(confirmAction.gateway)}
        />
      )}
      {confirmAction?.kind === "remove" && (
        <ConfirmModal
          title="Remove edge runtime"
          body={`Remove ${confirmAction.gateway.name}? Queued PLC commands for this runtime are deleted and the device can no longer connect. This cannot be undone.`}
          confirmLabel={busyID === `delete:${confirmAction.gateway.id}` ? "Removing…" : "Remove runtime"}
          danger
          busy={busyID === `delete:${confirmAction.gateway.id}`}
          onCancel={() => setConfirmAction(null)}
          onConfirm={() => removeGateway(confirmAction.gateway)}
        />
      )}
    </div>
  );
}

// Preserve the module's long-standing public surface. Helpers now live in
// platformEdgeRuntimes.model; RuntimeRow lives in
// platformEdgeRuntimes.components. Re-exported here so tests, index.js, and the
// Gateway AI keep importing from "./platform-edge-runtimes" unchanged.
export {
  AUTO_DETECT_HARDWARE_PROFILE,
  DEFAULT_EDGE_CLOUD_TRANSPORT,
  DEFAULT_EDGE_SYNC_POLICY,
  DEFAULT_EDGE_HARDWARE_PROFILE,
  EDGE_RUNTIME_HARDWARE_PROFILE_OPTIONS,
  EDGE_CLOUD_TRANSPORT_OPTIONS,
  edgeGatewayEnrollmentPayload,
  edgeGatewayEnrollmentSettingsPayload,
  edgeHardwareProfileByID,
  edgeHardwareProfileFromCatalog,
  enrollmentHardwareProfile,
  hardwareProfileOptions,
  normalizeHardwareProfileCatalog,
  runtimeHardwareView,
  normalizeEdgeSyncPolicy,
  hardwareIOGatewayIDFromSearch,
  runtimeConnectionRequestFromSearch,
  runtimeIOPresentation,
  runtimeSyncClassOptions,
  runtimeEnrollmentReadiness,
  runtimeReenrollmentSettings,
  edgeCommandDeadlineView,
  edgeGPIOBoardView,
  plcDeploymentSecurityView,
  plcBuildIdentityView,
  installerCommand,
} from "./platformEdgeRuntimes.model";
export { RuntimeRow } from "./platformEdgeRuntimes.components";
export { PlcEdgeRuntimesPage };
